/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import {
	classnames as cx,
	arrayFilters,
	propsUtils,
	keyboardUtils,
	lodash,
} from '@ba-ui-toolkit/utils';
import SearchInput from '@ba-ui-toolkit/searchinput';
import ProgressIndicator from '@ba-ui-toolkit/progressindicator';
import ListDropdown from '@ba-ui-toolkit/listdropdown';
import styles from './AutoCompleteInput.scss';
import stringResource from './nls/StringResource';

const { startsWithFilter, containsFilter } = arrayFilters;
const autoCompleteInputClassName = styles.autoCompleteInputClassName.replace(/"/g, '');
const { sanitize, getInheritedProps } = propsUtils;
const { getKeyName, keyNames } = keyboardUtils;
const { get, uniqueId, isEqual } = lodash;

export default class AutoCompleteInput extends Component {
	static propTypes = {
		...SearchInput.propTypes,
		values: PropTypes.oneOfType([
			PropTypes.arrayOf(PropTypes.string),
			PropTypes.arrayOf(PropTypes.object),
		]),
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
		loading: PropTypes.bool,
		fullWidth: PropTypes.bool,
		onChange: PropTypes.func,
		onValueMatch: PropTypes.func,
		onSelectionChange: PropTypes.func,
		onSubmit: PropTypes.func,
		onBlur: PropTypes.func,
		onKeyDown: PropTypes.func,
		collapseExtraOptions: PropTypes.object,
		listExtraOptions: PropTypes.object,
		progressIndicatorExtraOptions: PropTypes.object,
		filterFromStart: PropTypes.bool,
		ariaLabel: PropTypes.string,
		ariaLabelledBy: PropTypes.string,
		customFilter: PropTypes.func,
	};

	static defaultProps = {
		...SearchInput.defaultProps,
		filterFromStart: true,
		value: '',
		values: [],
		collapseExtraOptions: {},
		listExtraOptions: {},
		progressIndicatorExtraOptions: {},
		ariaLabel: stringResource.get('autoCompleteSearchInput'),
		ariaLabelledBy: '',
	};

	constructor(props) {
		super(props);
		this.state = {
			focusIndex: -1,
			filteredValues: this._filterValues(props),
			value: this._getStringValue(props.value),
			open: false,
		};
		this.textDir = props.textDir;
		this._id = uniqueId('__autocompleteinput_');
	}

	componentWillReceiveProps(nextProps) {
		const { values } = nextProps;
		const value = this._getStringValue(nextProps.value);

		if (value !== '') {
			// Defect 244355: Check when new props values is passed in and open dropdown if there are any matched values.
			const openState = !isEqual(values, this.props.values)
				? { open: this._hasFilteredValues(value, values) }
				: {};
			// Defect 249963: Update value state only when value props changed
			const newValue =
				value !== this._getStringValue(this.props.value) ? value : this.state.value;
			if (this.state.focusIndex !== -1) {
				this.setState({
					focusIndex: -1,
					value: newValue,
					filteredValues: this._filterValues(nextProps),
					...openState,
				});
			} else if (this.state.focusIndex === -1) {
				this.setState({
					value: newValue,
					filteredValues: this._filterValues(nextProps),
					...openState,
				});
			}
		} else {
			// reset filtered value list when text input is empty
			// Defect 239844: add value in set state to clear input field whenever empty string is passed to prop.value
			this.setState({ filteredValues: this._filterValues(nextProps), value });
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.state.focusIndex !== prevState.focusIndex && this._list) {
			this._list.scrollAround(this.state.focusIndex);
		}
	}

	focus() {
		this._input && this._input.focus();
	}

	_filterValues({ value = '', values = [] }) {
		const { customFilter } = this.props;

		let stringValue = this._getStringValue(value);
		if (stringValue === null) {
			stringValue = '';
		}

		if (customFilter) {
			// Story 252514: Add custom filter for server side auto-completion
			return customFilter({ value: stringValue.trim(), values });
		} else if (values && values.filter) {
			stringValue = stringValue.trim();
			const filterFn =
				stringValue.length === 1 && this.props.filterFromStart
					? startsWithFilter(stringValue)
					: containsFilter(stringValue);

			return values.filter(filterFn);
		}

		return [];
	}

	_getStringValue(value) {
		if (typeof value !== 'string' && value !== null) {
			return value.label;
		}

		return value;
	}

	_onSubmit = v => {
		let value;
		const currentStringValue = this.state.value;
		if (v) {
			value = v;
		} else if (this._arrayContainsObj(this.props.values)) {
			value = { value: currentStringValue, label: currentStringValue };
		} else {
			value = currentStringValue;
		}
		this.props.onSubmit && this.props.onSubmit(value);
	};

	_arrayContainsObj(arr) {
		for (let i = 0; i < arr.length; i++) {
			if (typeof arr[i] === 'object') {
				return true;
			}
		}

		return false;
	}

	_onSelectionChange = (val, name, index) => {
		const { filteredValues } = this.state;
		if (index >= 0) {
			const value = filteredValues[index];
			this.setState({ open: false, focusIndex: -1, value: this._getStringValue(value) });

			// expose string or object in callbacks
			this.props.onSelectionChange && this.props.onSelectionChange(value);
			this._onSubmit(value);
		}
	};

	_onActiveChange = index => {
		this.setState({
			focusIndex: index,
			value: this._getStringValue(this.state.filteredValues[index]),
		});
	};

	_onKeyDown = e => {
		const { focusIndex } = this.state;
		const { filteredValues } = this.state;
		const { SHIFT, ESCAPE, A, HOME, UP, DOWN, ENTER, SPACE } = keyNames;

		if (this.state.open) {
			switch (getKeyName(e)) {
				case SHIFT:
					break;
				case HOME:
					e.preventDefault();
					if (filteredValues && filteredValues.length) {
						const newFocusIndex = 0;
						this.setState({
							focusIndex: newFocusIndex,
							value: this._getStringValue(filteredValues[newFocusIndex]),
						});
					}
					break;
				case UP:
					e.preventDefault();
					if (filteredValues && filteredValues.length) {
						if (focusIndex === 0) {
							this.setState({
								focusIndex: -1,
								value: this._getStringValue(this.props.value),
							});
						} else if (focusIndex === -1) {
							const newFocusIndex = filteredValues.length - 1;
							this.setState({
								focusIndex: newFocusIndex,
								value: this._getStringValue(filteredValues[newFocusIndex]),
							});
						} else {
							this._handleKeyEventByList(e);
						}
					}
					break;
				case DOWN:
					e.preventDefault();
					if (filteredValues && filteredValues.length) {
						if (focusIndex === filteredValues.length - 1) {
							this.setState({
								focusIndex: -1,
								value: this._getStringValue(this.props.value),
							});
						} else if (focusIndex === -1) {
							this.setState({
								focusIndex: 0,
								value: this._getStringValue(filteredValues[0]),
							});
						} else {
							this._handleKeyEventByList(e);
						}
					}
					break;
				case ESCAPE:
					e.preventDefault();
					this.setState({ open: false, focusIndex: -1, value: '' });
					break;
				case SPACE:
					if (focusIndex !== -1) {
						this._handleKeyEventByList(e);
					}
					break;
				case A:
					// override key event handler for e.ctrlKey || e.metaKey in List.js
					break;
				case ENTER:
					if (focusIndex === -1) {
						this._onSubmit();
						this.setState({ open: false });
						break;
					}
				// falls through
				default:
					this._handleKeyEventByList(e);
			}
		} else {
			switch (getKeyName(e)) {
				case ENTER:
					this._onSubmit();
					break;
				case ESCAPE:
					e.preventDefault();
					this.setState({ focusIndex: -1, value: '' });
					break;
				case UP:
				// falls through
				case DOWN:
					// arrow down
					e.preventDefault();
					this.setState({ open: true });
					break;
				case SPACE:
					if (e.ctrlKey) {
						e.preventDefault();
						this.setState({ open: true });
					}
					break;
				default:
			}
		}

		this.props.onKeyDown && this.props.onKeyDown(e);
	};

	_notifyChange(value) {
		this.props.onChange && this.props.onChange(value);
	}

	_onChange = value => {
		const { values } = this.props;
		this._notifyChange(value);
		const hasFilteredValues = this._hasFilteredValues(value, values);
		this.setState({ open: hasFilteredValues, value: this._getStringValue(value) });
	};

	_hasFilteredValues(value, values) {
		const filteredValues = this._filterValues({ value, values });
		if (value.trim().length && filteredValues.length) {
			this.props.onValueMatch && this.props.onValueMatch(filteredValues);

			return true;
		}

		return false;
	}

	_onBlur = e => {
		// Defect 229379: Prevent dropdown from closing when user clicks on scroll arrow buttons
		const listNode = get(this, '_dropdown._list._node');
		if (
			(listNode &&
				(listNode.contains(document.activeElement) ||
					listNode.contains(e.relatedTarget))) ||
			e.target !== e.currentTarget
		) {
			this.focus();

			return;
		}

		if (this.state.open) {
			this.setState({ open: false });
		}
		this.props.onBlur && this.props.onBlur(e);
	};

	_triggerNodeRenderer = ref => {
		const {
			id,
			'data-tid': dataTid,
			fullWidth,
			iconLeft,
			hideLeftIcon,
			ariaLabel,
			ariaLabelledBy,
			loading,
			progressIndicatorExtraOptions,
			...others
		} = this.props;
		const autocompleteIcon =
			loading && !hideLeftIcon ? (
				<ProgressIndicator
					data-tid="ba-auto-complete__progress-indicator"
					{...sanitize(progressIndicatorExtraOptions, AutoCompleteInput)}
					variant="circle"
					tabIndex={-1}
					size="small"
				/>
			) : (
				iconLeft
			);
		const { value } = this.state;
		const searchInputPassthroughProps = getInheritedProps(others, SearchInput);

		return (
			<SearchInput
				aria-label={ariaLabel}
				ariaLabelledBy={ariaLabelledBy} // pass this to BaseInput
				aria-controls={this._id}
				aria-expanded={this.state.open}
				data-tid="ba-auto-complete__search-input"
				{...searchInputPassthroughProps}
				fullWidth
				iconLeft={autocompleteIcon}
				hideLeftIcon={hideLeftIcon}
				onChange={this._onChange}
				onBlur={this._onBlur}
				value={value}
				ref={node => {
					this._input = node;
					if (node) ref(findDOMNode(node));
				}}
				onKeyDown={this._onKeyDown}
			/>
		);
	};

	_parseFilteredValues = values => {
		const objArr = values.map(obj => {
			if (typeof obj === 'string') {
				return {
					label: obj,
					value: obj,
				};
			} else if (!obj.value && obj.label) {
				obj.value = obj.label;
			}

			return obj;
		});

		return objArr;
	};

	_dropdownRef = node => {
		this._dropdown = node;
	};

	_onClose = () => {
		this.setState({ open: false });
	};

	_handleKeyEventByList(e) {
		const list = get(this, '_dropdown._list');
		list && list.handleKeyEvent(e);
	}

	_onScrollClose = () => {
		this.setState({ open: false });
	};

	render() {
		const {
			id,
			'data-tid': dataTid,
			fullWidth,
			iconLeft,
			hideLeftIcon,
			className,
			style,
			...others
		} = this.props;
		const { filteredValues, focusIndex, open } = this.state;
		const classNames = cx(autoCompleteInputClassName, { fullWidth });
		const dropdownProps = {
			triggerNodeId: this._id,
			id,
			'data-tid': dataTid || 'ba-auto-complete__search-input',
			...sanitize(others, AutoCompleteInput),
			triggerNodeRenderer: this._triggerNodeRenderer,
			options: this._parseFilteredValues(filteredValues),
			open,
			className: `${classNames} ${className}`,
			ref: this._dropdownRef,
			focusIndex,
			onClose: this._onClose,
			onActiveChange: this._onActiveChange,
			onChange: this._onSelectionChange,
			onScrollClose: this._onScrollClose,
			truncated: true,
			style,
		};

		return <ListDropdown {...dropdownProps} />;
	}
}
